import {defineStore} from 'pinia'
import {NetworkService} from "~/utils/NetworkService";
import {userStore} from "~/store/userStore";
import type {SearchAuditLogRb} from "~/models/request/searchAuditLogRb";
import type {
    AuditLogEntry,
    AuditLogFilterOptions,
    AuditLogPermissions,
    AuditLogSearchResult
} from "~/models/auditLogEntry";

/**
 * Audit log reads.
 *
 * Every call goes through the Nuxt server proxies so the service secret stays
 * server side. Results are not persisted: the trail is read fresh, and a stale
 * cached page of audit records would be worse than no page at all.
 */
export const auditLogStore = defineStore({
    id: 'audit-log-store',
    persist: false,
    state: () => {
        return {
            entries: <AuditLogEntry[]>[],
            total: 0,
            page: 1,
            pageSize: 25,
            totalPages: 1,
            filterOptions: <AuditLogFilterOptions>{actions: [], categories: [], results: [], actors: []},
            permissions: <AuditLogPermissions>{canView: false, canExport: false, canConfigureRetention: false},
            retentionDays: <number | null>null
        }
    },
    actions: {
        searchAuditLog: async function (filters: SearchAuditLogRb): Promise<AuditLogSearchResult> {
            const ns = new NetworkService();
            const {Result} = await ns.post$fetch(
                '/api/audit-log/search', filters, null,
                "searchAuditLog" + new Date().getMilliseconds() + "_"
            );

            const result: AuditLogSearchResult = Result ?? {rows: [], total: 0, page: 1, pageSize: 25, totalPages: 1};
            this.entries = result.rows ?? [];
            this.total = result.total ?? 0;
            this.page = result.page ?? 1;
            this.pageSize = result.pageSize ?? 25;
            this.totalPages = result.totalPages ?? 1;
            return result;
        },

        loadFilterOptions: async function (companyId: number, from?: string, to?: string): Promise<AuditLogFilterOptions> {
            const ns = new NetworkService();
            const {Result} = await ns.post$fetch(
                '/api/audit-log/filters', {companyId, from, to}, null,
                "auditFilterOptions" + new Date().getMilliseconds() + "_"
            );
            this.filterOptions = Result ?? {actions: [], categories: [], results: [], actors: []};
            return this.filterOptions;
        },

        loadPermissions: async function (companyId: number): Promise<AuditLogPermissions> {
            const ns = new NetworkService();
            try {
                const {Result} = await ns.post$fetch(
                    '/api/audit-log/permissions', {companyId}, null,
                    "auditPermissions" + new Date().getMilliseconds() + "_"
                );
                this.permissions = Result ?? {canView: false, canExport: false, canConfigureRetention: false};
            } catch (e) {
                // Fail closed — an unreachable check must not open the page
                this.permissions = {canView: false, canExport: false, canConfigureRetention: false};
            }
            return this.permissions;
        },

        getEntityHistory: async function (companyId: number, entityType: string, entityId: string | number): Promise<AuditLogEntry[]> {
            const ns = new NetworkService();
            const {Result} = await ns.post$fetch(
                '/api/audit-log/entity-history', {companyId, entityType, entityId}, null,
                "auditEntityHistory" + new Date().getMilliseconds() + "_"
            );
            return Result?.rows ?? [];
        },

        loadRetention: async function (companyId: number): Promise<number | null> {
            const ns = new NetworkService();
            const {Result} = await ns.post$fetch(
                '/api/audit-log/retention', {companyId}, null,
                "auditRetention" + new Date().getMilliseconds() + "_"
            );
            this.retentionDays = Result?.retentionDays ?? null;
            return this.retentionDays;
        },

        updateRetention: async function (companyId: number, retentionDays: number | null): Promise<number | null> {
            const ns = new NetworkService();
            const {Result} = await ns.post$fetch(
                '/api/audit-log/update-retention', {companyId, retentionDays}, null,
                "auditUpdateRetention" + new Date().getMilliseconds() + "_"
            );
            this.retentionDays = Result?.retentionDays ?? null;
            return this.retentionDays;
        },

        /**
         * Export the current filter and hand the browser a file.
         *
         * The response is CSV, not the usual envelope, so this goes through
         * $fetch directly rather than NetworkService.
         */
        exportAuditLog: async function (filters: SearchAuditLogRb): Promise<void> {
            const useUserStore = userStore();
            const token = useUserStore.loggedInUser?.SessionToken;

            const csv = await $fetch<string>('/api/audit-log/export', {
                method: 'post',
                body: {...filters, st: token},
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                responseType: 'text'
            });

            const anchor = document.createElement('a');
            anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
            anchor.target = '_blank';
            anchor.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
            anchor.click();
        }
    }
})
